import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, Paper, Typography, Divider} from '@material-ui/core';
import {makeStyles} from "@material-ui/core/styles";
import DQLabLogo from '../../assets/images/DQLAB_logo.png'

const useStyles = makeStyles((theme) => ({
    question: {
        marginTop: theme.spacing(4),
        fontWeight: 'bold',
        fontSize:'42px',
        color: '#000000',
        textAlign: 'center',
    },
    nextButton: {
        marginTop: theme.spacing(4),
        backgroundColor: '#ff6468ff',
        color: '#ffffff !important',
        fontWeight: 'bold',
        fontSize: '18px',
        padding: '10px 50px !important',
        '&:hover': {
            backgroundColor: '#ff6468ff !important',
        },
    },
    logoContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing(3),
    },
    logo: {
        height: '85px',
    },
    divider: {
        margin: `${theme.spacing(3)}px 0`,
        backgroundColor: '#ddd',
    },
}));

const TermOfUse = ({setActiveStep, standalone = true, onAgree }) => {
    const classes = useStyles();
    const termsRef = useRef(null);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

    const checkIfScrollable = () => {
        if (!termsRef.current) return false;
        const {scrollHeight, clientHeight} = termsRef.current;
        return scrollHeight > clientHeight;
    };

    const handleScroll = () => {
        if (!termsRef.current) return;

        const {scrollTop, scrollHeight, clientHeight} = termsRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

        setHasScrolledToBottom(isAtBottom);
    };

    useEffect(() => {
        if (standalone) return;
        const currentRef = termsRef.current;
        if (currentRef) {
            const isScrollable = checkIfScrollable();
            if (!isScrollable) {
                setHasScrolledToBottom(true);
                return;
            }

            currentRef.addEventListener('scroll', handleScroll);
            handleScroll();
        }

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, [standalone]);

    return (
        <Box sx={{
            width: standalone ? '100%': undefined,
            maxWidth: standalone ? 1200 : 900,
            mx: standalone ? 'auto' : undefined,
            padding: standalone ? 0 : undefined
        }}>
            <Paper
                elevation={3}
                style={{
                    backgroundColor:  standalone ? 'transparent' : '#F8F5F5',
                    padding: standalone ? '40px' : '24px',
                    maxHeight: standalone ? 'none' : '400px',
                    overflowY: standalone ? 'visible' : 'auto',
                    marginBottom: standalone ? 0 : '24px',
                    border:  standalone ? 'none' : '1px solid #ddd',
                    boxShadow: 'none',
                    width: standalone ? '100%' : undefined
                }}
                ref={termsRef}
            >
                <div className={classes.logoContainer}>
                    <img src={DQLabLogo} alt="DQ Lab Logo" className={classes.logo}/>
                </div>
                <Typography variant={standalone ? 'h3' : 'h5'} style={{fontSize: standalone ? undefined : '32px'}} className={classes.question} gutterBottom>
                    Terms and Conditions for Powered by DQ
                </Typography>
                <br/>
                <Typography variant={standalone ? 'h6' : 'body1'} paragraph style={{fontWeight:'normal'}}>
                    Effective Date: May 20, 2025
                </Typography>
                <br/>
                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    These Terms and Conditions (“Terms”) govern your participation as a Powered by DQ User (“PBDQ User” or “you”) offered by DQ Lab Pte Ltd (“DQ Lab”, “DQ”, “we”, or “us”).
                </Typography>
                <Divider className={classes.divider}/>
                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>1. Appointment and Scope</strong><br/>
                    By enrolling in Powered by DQ, you are granted a non-exclusive, non-transferable, revocable license to
                    access and use the "Powered by DQ" platform solely to create, market, and sell DQ-approved programs
                    and assessments. You acknowledge that your role is that of an independent contractor. Nothing in
                    these Terms creates a joint venture, partnership, employment, or agency relationship. You shall have
                    no authority to make representations or enter into agreements on behalf of DQ Lab.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>2. Pricing and Revenue</strong><br/>
                    You may set your own license prices to sell to your clients, provided the price per user is not
                    lower than the minimum threshold set by DQ Lab. In return, you agree to pay DQ Lab a monthly usage
                    fee of per student license issued, with invoices issued monthly and payable within 30 days. Late
                    fees will accrue at 2% per month or the maximum amount permitted by law.<br/><br/>
                    You may also be eligible to receive special awards and commissions based on hitting certain targets
                    the DQ Lab may make available from time to time in its own discretion, it being understood said
                    awards and commissions may be changed by DQ Lab at any time in its sole discretion with no liability
                    to you.<br/><br/>
                    As a PBDQ User, you are solely responsible for all taxes, fees, or legal obligations related to your
                    earnings. DQ Lab shall not be responsible for any local, national, or international tax implications
                    of your income.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>3. Ownership of Intellectual Property</strong><br/>
                    All technology, content, data, reports, documentation, tools, materials, designs, processes,
                    algorithms, and other intellectual property (collectively, the "IP") made available through or
                    developed within the Powered by DQ platform is and shall remain the <strong>exclusive property of DQ
                    Lab.</strong> Any content, customizations, or assessments created using the platform - including derivative works
                    - shall be deemed <strong>work-for-hire</strong> and the resulting IP shall vest solely in DQ Lab.
                    You <strong>may not claim moral rights or ownership</strong> over any component or product derived
                    from or developed using the Powered by DQ tools.<br/><br/>
                    You agree not to:<br/>
                    <ul style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>
                            Claim, register, or assert ownership over any part of the platform or output;
                        </li>
                        <li>
                            Replicate, reverse-engineer, modify, or attempt to replicate any aspect of the platform; or
                        </li>
                        <li>
                            License, sublicense, assign, resell, or share the tools or assessments outside the permitted
                            scope.
                        </li>
                    </ul>
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>4. Confidentiality</strong><br/>
                    You agree to treat as confidential any non-public information disclosed by DQ Lab, including but not
                    limited to sales figures, marketing strategies, product roadmaps, business models, and contractual
                    terms (collectively, the "Confidential Information"). You shall: <br/>
                    <ul style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>
                            Not disclose such Confidential Information to any third party;
                        </li>
                        <li>
                            Use it only for purposes directly related to your participation in the Program; and
                        </li>
                        <li>
                            Take reasonable security measures to prevent unauthorized access.
                        </li>
                    </ul>
                    This obligation survives termination of your participation in the Program.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>5. Use of Platform and Content</strong><br/>
                    You are granted a <strong>limited, non-exclusive, revocable license </strong> to use the Powered by
                    DQ
                    platform solely to create and distribute assessments that meet DQ Lab's quality, ethical, and brand
                    standards.
                    All content is subject to review, approval, and audit by DQ Lab. If requested by DG Lab, you must
                    make any modifications
                    requested by DQ Lab or take down any content promptly, Misuse or breach of these guidelines may
                    result in immediate termination.<br/> <br/>
                    You must not:
                    <ul style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>Alter platform branding or remove "Powered by DQ" identifiers;</li>
                        <li>Use the platform in violation of any law or regulation;</li>
                        <li>Compete with the platform by offering a similar service to any of your customers during the
                            term or for one year following termination of this agreement; or
                        </li>
                        <li>Incorporate unauthorized content, plagiarized materials, or harmful elements.</li>
                    </ul>
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>6. Branding and Representation</strong><br/>
                    All promotional materials, sales pages, and marketing campaigns must prominently include the
                    "Powered by DQ" brand, unless a separate written agreement states otherwise. You must: <ul
                    style={{marginTop: 0, paddingLeft: '24px'}}>
                    <li>Clearly disclose your role as a Powered by DQ User;</li>
                    <li>Avoid misleading, exaggerated, or unverified claims about DQ Lab; and</li>
                    <li>Refrain from suggesting a formal partnership or employment relationship with DQ Lab.</li>
                </ul>
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>7. Prohibited Conduct</strong><br/>
                    You agree not to:
                    <br/>
                    <ul style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>Use the platform to engage in illegal, unethical, or deceptive activities;</li>
                        <li>Share platform access with unapproved third parties;</li>
                        <li>Create offensive, discriminatory, or infringing assessments;</li>
                        <li>Misrepresent your identity, authority, or affiliation with DQ Lab; or</li>
                        <li>Collect or misuse data in violation of applicable laws.</li>
                    </ul>
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>8. Data Privacy and Security</strong><br/>
                    You are solely responsible for complying with all relevant data protection and privacy laws,
                    including but not limited to the <strong>Singapore Personal Data Protection Act, the EU General Data
                    Protection Regulation (GDPR)</strong>, and / or any other applicable data privacy regulations. You
                    must:
                    <br/>
                    <ul style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>Protect all user data collected via your assessments;</li>
                        <li>Avoid unauthorized sharing or transfer of data; and</li>
                        <li>Report any suspected data breaches immediately.</li>
                    </ul>
                    <br/>
                    <strong>DQ Lab shall not be held liable</strong> for any breach, leak, or misuse of data that occurs
                    within your systems, tools, or organization. You assume full responsibility for safeguarding all
                    data collected through your use of the Powered by DQ tools.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>Commitment to Confidentiality and Data Protection</strong><br/>
                    In the following, we would like to draw your attention to the legal provisions on confidentiality
                    that you must comply with in the course of your work as a Powered by DQ User. Your obligation continues without
                    a time limit and even after you have ceased to fulfil this function.
                    <br/><br/>
                    Claims for damages may arise from culpable breaches of this obligation. Data protection violations are
                    also threatened with potentially very high fines for the company, which may lead to claims for compensation
                    against you. We are obliged to inform you that in some limited cases, violations of this obligation can also
                    be punished with a fine and/or imprisonment in certain jurisdictions.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>Maintaining confidentiality and respect for data protection</strong><br/>
                    As you may come into contact with personal data in the course of your work, we hereby oblige you to observe
                    data protection, in particular to maintain confidentiality.
                    <br/><br/>
                    Your obligation is comprehensive. You may not process personal data yourself without authorisation and you may
                    not disclose or make this data available to other persons without authorisation. In particular, you are obliged to
                    observe the data protection regulations and instructions of DQ Lab and the jurisdictions where you operate.
                    <br/><br/>
                    As a reference point, according to Art. 5 GDPR, personal data must be:
                    <br/>
                    <ol type="a" style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>processed lawfully, fairly and in a transparent manner in relation to the data subject ('lawfulness, fairness and transparency');
                        </li>
                        <li>collected for specified, explicit and legitimate purposes and not further processed in a manner that is
                            incompatible with those purposes; further processing for archiving purposes in the public interest, scientific or
                            historical research purposes or statistical purposes shall, in accordance with Article 89(1), not be considered to
                            be incompatible with the initial purposes ('purpose limitation');
                        </li>
                        <li>adequate, relevant and limited to what is necessary in relation to the purposes for which they are processed ('data minimisation');
                        </li>
                        <li>accurate and, where necessary, kept up to date; every reasonable step must be taken to ensure that personal data that are inaccurate,
                            having regard to the purposes for which they are processed, are erased or rectified without delay ('accuracy');
                        </li>
                        <li>kept in a form which permits identification of data subjects for no longer than is necessary for the purposes for which the personal data are processed; personal data may be stored for longer periods insofar as the personal data will be processed solely for archiving purposes in the public interest, scientific or historical research purposes or statistical purposes in accordance with Article 89(1) subject to implementation of the appropriate technical and organisational measures required by this Regulation in order to safeguard the rights and freedoms of the data subject ('storage limitation');
                        </li>
                        <li>processed in a manner that ensures appropriate security of the personal data, including protection against unauthorised or unlawful processing and against accidental loss, destruction or damage, using appropriate technical or organisational measures ('integrity and confidentiality').
                        </li>
                    </ol>
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>Obligation to maintain business secrets</strong><br/>
                    You are obliged to maintain confidentiality about confidential matters of DQ Lab, such as details of
                    the company, as well as about business transactions. This holds true even after termination of the Powered by DQ
                    relationship, unless the information has become public knowledge. Such confidential
                    information includes, among others, the details on the schools participating in the platform as well
                    as details on the assessment and programs from Powered by DQ and internal processes at DQ Lab. All
                    electronic or physical records, transcripts, business documents, photocopies, official or business
                    transactions relating to your activities as a Powered by DQ User must be protected from access by unauthorised
                    persons.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>9. Limitation of Liability & Indemnity</strong><br/>
                    To the maximum extent permitted by law, <strong> DQ Lab shall not be liable </strong>for:
                    <br/>
                    <ul style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>Any indirect, incidental, special, punitive, or consequential damages;</li>
                        <li>Loss of revenue, reputation, business opportunities, or data;</li>
                        <li>Any legal claims arising from your use, misuse, or representation of the Program.</li>
                    </ul>
                    <br/>
                    You agree to <strong>indemnify and hold harmless</strong> DQ Lab, its directors, officers, employees, and affiliates against any claims, losses, damages, or legal fees arising from your participation in the Program, including but not limited to IP violations, data misuse, or breach of confidentiality.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>10. Termination</strong><br/>
                    DQ Lab reserves the right to suspend or terminate your participation in the Program at any time, for any reason, with or without prior notice. Upon termination, you must:
                    <br/>
                    <ul style={{marginTop: 0, paddingLeft: '24px'}}>
                        <li>Cease all use of the Powered by DQ platform;</li>
                        <li>Remove all "Powered by DQ" branding from your materials;</li>
                        <li>Return or destroy any confidential information as instructed.</li>
                    </ul>
                    <br/>
                    No compensation, refund, or damages shall be payable for termination.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>11. Changes to Terms</strong><br/>
                    DQ Lab may update or modify these Terms at any time. You will be notified of material changes.
                    Continued participation in the Program after such updates constitutes acceptance of the revised
                    Terms.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>12. Governing Law and Jurisdiction</strong><br/>
                    These Terms shall be governed by and construed in accordance with the laws of <strong>Singapore</strong>. Any disputes shall be subject to the exclusive jurisdiction of the Singapore International Arbitration Centre (SIAC) and subject to SAIC Rules.
                </Typography>
                <Divider className={classes.divider}/>

                <Typography variant={standalone ? 'h6' : 'body1'} style={{fontWeight:'normal'}} paragraph>
                    <strong>By participating as a Powered by DQ User, you acknowledge that you have read, understood, and agreed to these Terms and Conditions.</strong><br/>
                    For inquiries, <a href="mailto:contact@dqforall.com">contact@dqforall.com</a>.

                </Typography>
            </Paper>

            {!standalone && (
                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onAgree}//{() => setActiveStep((prev) => prev + 1)}
                        disabled={!hasScrolledToBottom}
                        className={classes.nextButton}
                    >
                        I Agree to the Terms and Conditions
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default TermOfUse;